<template>
    <v-container fluid class="fill-height" style="background-color: var(--v-theme-background);">
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4">
          <v-card class="elevation-12 pa-3 rounded-lg">
            <v-toolbar color="appbar" flat>
              <v-toolbar-title class="text-h5 font-weight-bold primary--text d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-account-plus-outline</v-icon>
                Create Account
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text class="pa-sm-6 pa-4">
              <v-alert v-if="authStore.error" type="error" density="compact" variant="tonal" class="mb-4">
                {{ authStore.error }}
              </v-alert>
              <v-form ref="registerForm" v-model="isFormValid" @submit.prevent="handleRegister">
                <v-text-field
                  v-model="email"
                  label="Email Address"
                  prepend-inner-icon="mdi-email-outline"
                  variant="outlined"
                  type="email"
                  :rules="[rules.required, rules.email]"
                  required
                  class="mb-4"
                  color="primary"
                  @input="authStore.clearError()"
                ></v-text-field>

                <v-text-field
                  v-model="first_name"
                  label="First Name"
                  prepend-inner-icon="mdi-account-outline"
                  variant="outlined"
                  type="text"
                  :rules="[rules.required]"
                />

                <v-text-field
                  v-model="last_name"
                  label="Last Name"
                  prepend-inner-icon="mdi-account-outline"
                  variant="outlined"
                  type="text"
                  :rules="[rules.required]"
                />

                <v-select
              :items="companies"
              item-title="name"
              item-value="id"
              v-model="company_id"
              label="Company"
              variant="outlined"
              class="mb-2"
              :rules="[rules.required]"
            />
  
                <v-text-field
                  v-model="password"
                  label="Password"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="showPassword = !showPassword"
                  :rules="[rules.required, rules.minLength(8)]"
                  required
                  class="mb-4"
                  color="primary"
                  hint="Minimum 8 characters"
                  @input="authStore.clearError()"
                ></v-text-field>
  
                <v-text-field
                  v-model="confirmPassword"
                  label="Confirm Password"
                  prepend-inner-icon="mdi-lock-check-outline"
                  variant="outlined"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="showConfirmPassword = !showConfirmPassword"
                  :rules="[rules.required, rules.passwordMatch]"
                  required
                  class="mb-6"
                  color="primary"
                  @input="authStore.clearError()"
                ></v-text-field>
  
                <CaptchaField v-model:captchaAnswer="captchaAnswer" />

                <v-btn
                  :loading="authStore.loading"
                  :disabled="!isFormValid || authStore.loading"
                  block
                  color="primary"
                  size="large"
                  type="submit"
                  class="py-3"
                >
                  Register
                </v-btn>
              </v-form>
            </v-card-text>
             <v-card-actions class="justify-center pa-4">
               <span class="text-caption text-disabled">Already have an account?</span>
               <router-link :to="{ name: 'Login' }" class="text-primary text-caption font-weight-medium text-decoration-none ml-1">
                  Login
                </router-link>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </template>
  
  <script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useAuthStore } from '@/stores/auth';
  import CaptchaField from './CaptchaField.vue';
  import apiClient from '@/services/api';
  
  const authStore = useAuthStore();
  
  const registerForm = ref(null);
  const isFormValid = ref(false);
  const email = ref('');
  const password = ref('');
  const confirmPassword = ref('');
  const showPassword = ref(false);
  const showConfirmPassword = ref(false);
  const captchaAnswer = ref('');
  const company_id = ref('');
  const companies = ref([]);

  onMounted(async () => {
    const res = await apiClient.get('/companies');
    companies.value = res.data;
  });

  const rules = {
    required: value => !!value || 'This field is required.',
    email: value => /.+@.+\..+/.test(value) || 'E-mail must be valid.',
    minLength: len => value => (value && value.length >= len) || `Minimum ${len} characters.`,
    passwordMatch: computed(() => value => value === password.value || 'Passwords do not match.'),
  };
  
  const handleRegister = async () => {
    if (!registerForm.value) return;
    const { valid } = await registerForm.value.validate();
  
    if (valid) {
      if (!await validateCaptcha()) {
        authStore.setError('Invalid captcha answer');
        return;
      }

      await authStore.register({ email: email.value, password: password.value, company_id: company_id.value });
      // Navigation is handled by the authStore on success
    }
  };

  async function validateCaptcha() {
    const res = await apiClient.post('/captcha/validate', {
      answer: captchaAnswer.value
    })
    return res.data.valid
  }
  </script>
  
  <style scoped>
  .fill-height {
    min-height: calc(100vh - 64px); /* Adjusted for no footer on register page */
  }
  .primary--text { color: var(--v-theme-primary) !important; }
  .text-primary { color: var(--v-theme-primary) !important; }
  .v-card { border-top: 4px solid var(--v-theme-primary); }
  .v-toolbar-title { line-height: 1.5rem; }
  </style>
  